@file:Suppress("MagicNumber", "LongMethod")

package com.nonopn.skydome.common.app.pages.densities

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.cert_nsg_title
import com.nonopn.skywatcher.common.app.res.keraunic_report_title
import com.nonopn.skywatcher.common.app.res.menu_sr_certificates
import org.jetbrains.compose.resources.stringResource
import kotlin.uuid.ExperimentalUuidApi

private object Colors {
    val Primary = Color(0xFF2B7FE0)
    val Text = Color(0xFF1A2B4A)
    val Card = Color(0xFFEEF2F8)
    val Muted = Color(0xFF9AADC0)
    val Certificate = Color(0xFF22A25B)
    val LightningCertificate = Color(0xFFE65100)
}

@OptIn(ExperimentalUuidApi::class)
@Composable
fun RenderDensityActivity(
    modifier: Modifier = Modifier,
    project: Project,
    activities1Km: Int,
    activities2Km: Int,
    onNavigateKeraunic: ((projectUuid: String, projectName: String, lat: Double, lon: Double) -> Unit)? = null,
    onNavigateCertNsg: ((projectUuid: String, projectName: String, lat: Double, lon: Double) -> Unit)? = null,
    onNavigateLightningCertificates: (() -> Unit)? = null,
) {
    Card(
        modifier = modifier.padding(4.dp).fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        shape = RoundedCornerShape(8.dp),
        elevation = CardDefaults.cardElevation(4.dp)
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            // Project name
            Text(
                project.name,
                fontWeight = FontWeight.Bold,
                fontSize = 15.sp,
                color = Colors.Text,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            HorizontalDivider(color = Colors.Card)

            // 1km / 2km strike activities
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                listOf(
                    Triple(activities1Km, "1 km", FontWeight.Bold),
                    Triple(activities2Km, "2 km", FontWeight.Normal)
                ).forEach { (count, radius, weight) ->
                    Column(
                        Modifier.weight(1f).clip(RoundedCornerShape(8.dp)).padding(8.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            "$count",
                            fontSize = 22.sp,
                            fontWeight = FontWeight.ExtraBold,
                            color = Colors.Primary
                        )
                        Text("impacts", fontSize = 10.sp, color = Colors.Muted)
                        Text(
                            "radius $radius",
                            fontSize = 11.sp,
                            color = Colors.Text,
                            fontWeight = weight
                        )
                    }
                }
            }

            // Navigation buttons
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedButton(
                        modifier = Modifier.weight(1f),
                        onClick = {
                            onNavigateKeraunic?.invoke(
                                project.uuid,
                                project.name,
                                project.latitude,
                                project.longitude
                            )
                        },
                        colors = ButtonDefaults.outlinedButtonColors(contentColor = Colors.Primary)
                    ) {
                        Text(
                            "📊 ${stringResource(Res.string.keraunic_report_title)}",
                            fontSize = 11.sp,
                            maxLines = 1
                        )
                    }
                    OutlinedButton(
                        modifier = Modifier.weight(1f),
                        onClick = {
                            onNavigateCertNsg?.invoke(
                                project.uuid,
                                project.name,
                                project.latitude,
                                project.longitude
                            )
                        },
                        colors = ButtonDefaults.outlinedButtonColors(contentColor = Colors.Certificate)
                    ) {
                        Text(
                            "📄 ${stringResource(Res.string.cert_nsg_title)}",
                            fontSize = 11.sp,
                            maxLines = 1
                        )
                    }
                }
                OutlinedButton(
                    modifier = Modifier.fillMaxWidth(),
                    onClick = { onNavigateLightningCertificates?.invoke() },
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = Colors.LightningCertificate)
                ) {
                    Text(
                        "⚡ ${stringResource(Res.string.menu_sr_certificates)}",
                        fontSize = 11.sp,
                        maxLines = 1
                    )
                }
            }
        }
    }
}
